import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import useQueryGetActiveTestSessions from '@/api/user/getActiveTestSessions.ts'
import useQueryGetMyPermission, { PermissionAction, PermissionSubject } from '@/api/auth/getPermission.ts'
import ClockIcon from '@/assets/ic_clock.svg?react'
import ChevronDownIcon from '@/assets/ic_chevron_down.svg?react'
import ResponsivePopover from '@/component/testMode/ResponsivePopover.tsx'
import TestSessionRow from '@/component/testMode/TestSessionRow.tsx'
import useTestSessionActions from '@/component/testMode/useTestSessionActions.tsx'
import { sortByEndsAt } from '@/util/testSession.ts'

const SHEET_MAX_WIDTH = 767

const TestSessionChip: React.FC = () => {
    const { t } = useTranslation()
    const { data: sessions = [] } = useQueryGetActiveTestSessions()
    const { hasPermission } = useQueryGetMyPermission()
    const { end, isBusy } = useTestSessionActions()
    const [isOpen, setIsOpen] = useState(false)

    useEffect(() => {
        if (sessions.length === 0) setIsOpen(false)
    }, [sessions.length])

    if (sessions.length === 0) return null

    const canEdit = hasPermission(PermissionSubject.ALERT_EMERGENCY, PermissionAction.UPDATE)
    const sorted = sortByEndsAt(sessions)
    const value = sorted.length === 1 ? sorted[0].protecteeName : t('testMode.chip.count', { count: sorted.length })

    return (
        <div className={'care-dashboard__test-chip'}>
            <ResponsivePopover
                isOpen={isOpen}
                onOpenChange={setIsOpen}
                placement={'bottom-start'}
                sheetMaxWidth={SHEET_MAX_WIDTH}
                ariaLabel={t('testMode.list.title', { count: sorted.length })}
                surfaceClassName={'test-mode-popover--list'}
                renderTrigger={({ ref, props }) => (
                    <button
                        ref={ref}
                        type={'button'}
                        className={'test-mode-chip'}
                        aria-expanded={isOpen}
                        {...props}>
                        <ClockIcon className={'test-mode-chip__icon'} aria-hidden />
                        <span className={'test-mode-chip__label'}>{t('testMode.chip.label')}</span>
                        <span className={'test-mode-chip__value'}>{value}</span>
                        <ChevronDownIcon
                            className={`test-mode-chip__chevron ${isOpen ? 'is-open' : ''}`}
                            aria-hidden
                        />
                    </button>
                )}>
                <section className={'test-session-list'}>
                    <header className={'test-session-list__head'}>
                        <strong>{t('testMode.list.title', { count: sorted.length })}</strong>
                        <span>{t('testMode.list.desc')}</span>
                    </header>
                    <ul className={'test-session-list__rows app-scrollbar'}>
                        {sorted.map((session, index) => (
                            <TestSessionRow
                                key={session.id}
                                name={session.protecteeName}
                                serial={session.deviceSerial}
                                meta={t('testMode.list.meta', { owner: session.createdByName })}
                                endsAt={session.endsAt}
                                canEnd={canEdit}
                                isBusy={isBusy(session.protecteeId)}
                                emphasizeRemaining={index === 0 && sorted.length > 1}
                                onEnd={() => end(session)}
                            />
                        ))}
                    </ul>
                    <footer className={'test-session-list__footer'}>{t('testMode.list.footer')}</footer>
                </section>
            </ResponsivePopover>
        </div>
    )
}

export default TestSessionChip
